import Calendar from '@/components/calendar/Calendar';
import { Button } from '@/components/common/Button';
import { Carousel, CarouselContent, CarouselItem } from '@/components/common/Carousel';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/common/Form';
import { Input } from '@/components/common/Input';
import { Label } from '@/components/common/Label';
import { Switch } from '@/components/common/Switch';
import { Textarea } from '@/components/common/Textarea';
import SearchInput from '@/components/general/SearchInput';
import StepTimeSelect from '@/components/general/StepTimeSelect';
import { useStore } from '@/hooks/useStore';
import yup from '@/services/yup';
import { loadFilePreview, loadURLPreview } from '@/utils/browsers';
import { checkImageSupportFormat, combineDateAndTime } from '@/utils/utils';
import { yupResolver } from '@hookform/resolvers/yup';
import { ImagePlusIcon, PlusIcon, Trash2Icon, X, XIcon } from 'lucide-react';
import { observer } from 'mobx-react-lite';
import { useEffect, useState } from 'react';
import { useForm, UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import StarRating from './StarRating';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/common/Popover';
import { flowResult } from 'mobx';
import { toastify } from '@/utils/toastify';

const validateSchema = yup.object().shape({
    seedingComments: yup.array().of(
        yup.object().shape({
            nameRewiewerSeedingComment: yup.string().required('form_error_validate.required'),
            incognito: yup.boolean(),
            dateReview: yup.date().nullable(),
            timeReview: yup.string().required(),
            serviceQuality: yup
                .number()
                .required('form_error_validate.required')
                .min(1, 'form_error_validate.required'),
            comment: yup
                .string()
                .max(2000)
                .nullable(),
            images: yup
                .array()
                .of(yup.mixed<string | File>().required('form_error_validate.required'))
                .checkFileImagesOfArrayExt('form_error_validate.shop_image_upload_format')
                .max(5, 'form_error_validate.post_review_image_upload_limit')
                .nullable()
        }).required()
    ).min(0).max(7)
});

type FormData = yup.InferType<typeof validateSchema>;
interface Shops {
    id: number,
    name: string
}

interface AddSeedingCommentProps {
    onSuccess?: () => void // Thêm prop onSuccess
}

export default observer(function AddSeedingComment({ onSuccess }: AddSeedingCommentProps) {
    // hooks
    const { t, i18n: { language } } = useTranslation();

    // store
    const {
        modalStore: { showAlertModal, hideModal },
        shopStore: { shopsSearchSeeding, getAllShopSearchSeeding },
        reviewStore: { postSeedingComments }
    } = useStore();
    const [openPopoverSearch, setOpenPopoverSearch] = useState<boolean>(false);
    const [listShop, setListShop] = useState<any[]>([]);
    const [searchValue, setSearchValue] = useState<string>('');

    useEffect(() => {
        if (shopsSearchSeeding?.length > 0 && searchValue !== '') {
            setOpenPopoverSearch(true);
        }
    }, [searchValue]);

    const form = useForm<FormData>({
        resolver: yupResolver(validateSchema),
        mode: 'onChange',
        defaultValues: {
            seedingComments: [
                {
                    nameRewiewerSeedingComment: '',
                    incognito: false,
                    dateReview: new Date(),
                    timeReview: '00:00',
                    serviceQuality: 5,
                    comment: null,
                    images: []
                }
            ]
        }
    });

    const watchOperationTimes = form.watch('seedingComments');

    const onSubmit = async (data: FormData) => {
        if (listShop.length === 0) {
            showAlertModal({
                type: 'error',
                content: t('sentences.min_shop_list')
            });
            return;
        }

        const seedingComments = data.seedingComments;
        const seedingCommentsPayload = seedingComments?.map(item => ({
            comment: item.comment,
            images: item.images,
            incognito: item.incognito,
            nameRewiewerSeedingComment: item.nameRewiewerSeedingComment,
            serviceQuality: item.serviceQuality,
            dateReview: combineDateAndTime(item.dateReview, item.timeReview)
        }));

        const payload = {
            shopIds: listShop.map((item: Shops) => item.id),
            seedingComments: seedingCommentsPayload
        };

        const res = await flowResult(postSeedingComments(payload));

        if (res) {
            toastify('success', t('messages.add_success'));
            hideModal(); // Đóng modal
            if (onSuccess) {
                onSuccess(); // Gọi callback để reload data table
            }
        }
    };

    const validateForcus = async () => {
        // Hàm đệ quy để tìm trường đầu tiên không hợp lệ
        const findFirstErrorField = (errors: any, parentKey = ''): string | null => {
            for (const key in errors) {
                const currentKey = parentKey ? `${parentKey}.${key}` : key;
                if (errors[key]?.message) {
                    return currentKey; // Trả về key của trường đầu tiên có lỗi
                }
                if (typeof errors[key] === 'object') {
                    const nestedKey = findFirstErrorField(errors[key], currentKey);
                    if (nestedKey) {
                        return nestedKey;
                    }
                }
            }
            return null;
        };

        // Tìm trường đầu tiên không hợp lệ
        const firstErrorField = findFirstErrorField(form.formState.errors);

        if (firstErrorField) {
            console.log('Focusing on:', firstErrorField); // Debug để kiểm tra key

            // Focus vào trường đầu tiên không hợp lệ
            form.setFocus(firstErrorField as keyof FormData);

            // Cuộn đến trường được focus
            const fieldElement = document.querySelector(`[name="${firstErrorField}"]`);
            if (fieldElement) {
                fieldElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
        }
    };

    const submit = async (data: FormData) => {
    };

    const handleAddEditByOfDate = () => {
        if (watchOperationTimes && watchOperationTimes.length < 5) {
            form.setValue('seedingComments', [...form.getValues('seedingComments') || [], {
                nameRewiewerSeedingComment: '',
                incognito: false,
                dateReview: new Date(),
                timeReview: '00:00',
                serviceQuality: 5,
                comment: null,
                images: []
            }]);
        }
    };

    const handleRemoveShop = (index: number) => {
        setListShop((prevListShop) => {
            return [...prevListShop.slice(0, index), ...prevListShop.slice(index + 1)];
        });
    };

    const handleSearch = async (value: string) => {
        const res = await flowResult(getAllShopSearchSeeding({ name: value, page: 1, size: 50, shopIdsHasReadStringify: JSON.stringify(listShop.map((item: Shops) => item.id)) }));
    };

    return (
        <>
            <Form {...form}>
                <form onSubmit={form.handleSubmit(submit)} className='my-4'>
                    <div className='flex min-h-[600px] border-[1px] border-[#e9e9e9]'>
                        <div className='w-[350px] bg-[#f2f2f2] p-2'>
                            <div className='text-[18px] text-[#333333] font-semibold pb-1'>{t('words_title.seeding_cmt_title_shop')}</div>
                            <div className='text-[12px] text-[#7F7F7F] pb-2'>{t('words_title.seeding_cmt_description_shop')}</div>
                            <Popover open={openPopoverSearch}>
                                <PopoverTrigger className='w-full'>
                                    <Input
                                        className='h-[30px] '
                                        value={searchValue}
                                        onChange={(e) => {
                                            setSearchValue(e.target.value);
                                            handleSearch(e.target.value);
                                            setOpenPopoverSearch(true);
                                        }}
                                        placeholder={t('words_title.seeding_cmt_placehoder_search_shop')}
                                        disabled={listShop.length >= 10}
                                        maxLength={255}
                                    />
                                </PopoverTrigger>
                                <PopoverContent
                                    className='w-[332px] p-1 max-h-[380px] overflow-auto'
                                    align='start'
                                    autoFocus={false}
                                    widthFull={true}
                                    onOpenAutoFocus={e => e.preventDefault()}
                                    onPointerDownOutside={() => setOpenPopoverSearch(false)}
                                >
                                    <div className='divide-y divide-gray-200'>
                                        {
                                            (shopsSearchSeeding && shopsSearchSeeding.length > 0) ?
                                                shopsSearchSeeding.map((shop, index) => (
                                                    <div
                                                        key={index}
                                                        className='py-2 px-1 text-sm flex items-center gap-2 hover:cursor-pointer hover:bg-slate-200'
                                                        onClick={() => {
                                                            setListShop(prevListShop => [...prevListShop, { id: shop?.id, name: shop?.name }]);
                                                            setSearchValue('');
                                                            setOpenPopoverSearch(false);
                                                        }}
                                                        title={shop?.name}
                                                    >
                                                        <span className='truncate-text'>{shop?.name}</span>
                                                    </div>
                                                )) :
                                                (
                                                    <div
                                                        className='py-2 px-1 text-sm flex items-center h-[100px]'
                                                    >
                                                        <div className='text-center w-full'>{t('words_title.seeding_cmt_no_search_shop')}</div>
                                                    </div>
                                                )
                                        }
                                    </div>
                                </PopoverContent>
                            </Popover>
                            <div className='border-t-[2px] mt-2 pt-1'>
                                <div className='flex justify-between'>
                                    <div className='text-[12px] text-[#7F7F7F]'>
                                        {t('words_title.seeding_cmt_total_shop', { total: listShop.length })}
                                    </div>
                                    <div className='text-[12px] text-[#169BD5] cursor-pointer' onClick={() => setListShop([])}>
                                        {t('words_title.seeding_cmt_clear')}
                                    </div>
                                </div>
                                <div>
                                    {listShop.length > 0 && listShop.map((item, index) => (
                                        <div className='flex justify-between border-b border-dashed border-[#afafaf] py-2 font-medium' key={index}>
                                            <div className='truncate-text'>
                                                {item.name}
                                            </div>
                                            <div className='cursor-pointer relative bottom-[-2px]' onClick={() => handleRemoveShop(index)}><X /></div>
                                        </div>
                                    ))}

                                </div>
                            </div>
                        </div>

                        <div className='grow max-w-[500px]'>
                            <div className='max-h-[650px] overflow-auto'>
                                {
                                    watchOperationTimes && watchOperationTimes.length > 0 && watchOperationTimes.map((item, index) => {
                                        return (
                                            <EditByDayOfWeekItem key={index} index={index} form={form} totalBox={watchOperationTimes.length} />
                                        );
                                    })
                                }

                                <div className='flex justify-center'>
                                    {watchOperationTimes && watchOperationTimes.length < 5 && (
                                        <button
                                            onClick={handleAddEditByOfDate}
                                            type='button'
                                            className='rounded-[50px] border border-[#9A6051] bg-[#fff] text-[#9A6051] text-[14px] font-semibold px-2 py-1 mt-3 flex items-center'
                                        >
                                            <PlusIcon className='w-6 h-6 text-[#9A6051]' />
                                            {t('words_title.seeding_cmt_btn_add_box')}
                                        </button>
                                    )}
                                </div>
                            </div>
                        </div>

                    </div>
                    <div className='mt-[1rem] w-1/2 mx-auto gap-10 lg:mt-[1.5rem] flex mb-5'>
                        <Button onClick={() => hideModal()} type='button' className='w-1/2' variant='outline'>{t('button.cancel')}</Button>
                        <Button
                            className='w-1/2'
                            onClick={async () => {
                                await form.handleSubmit(onSubmit)();
                                await validateForcus();
                            }}
                        >
                            {t('button.save')}
                        </Button>
                    </div>
                </form>
            </Form>
        </>
    );
});

interface EditByDayOfWeekItemProps {
    form: UseFormReturn<FormData>,
    index: number,
    totalBox: number
}

const EditByDayOfWeekItem = ({ form, index, totalBox }: EditByDayOfWeekItemProps) => {
    const { t } = useTranslation();

    const {
        modalStore: { showAlertModal, hideModal }
    } = useStore();

    const watchImages = form.watch(`seedingComments.${index}.images`);
    const watchToDateReview = form.watch(`seedingComments.${index}.dateReview`);

    useEffect(() => {
        if (Array.isArray(watchImages) && watchImages.length > 0) {
            if (watchImages instanceof Array) {
                watchImages.forEach((image, _index) => {
                    if (image instanceof File) {
                        loadFilePreview(image, `preview-image-${index}-${_index}`);
                    } else {
                        loadURLPreview(image, `preview-image-${index}-${_index}`);
                    }
                });
            }
        }
    }, [watchImages]);

    const handleRemoveOperationItem = () => {
        const operationTimes = form.getValues('seedingComments') ?? [];
        form.setValue('seedingComments', [
            ...operationTimes.slice(0, index),
            ...operationTimes.slice(index + 1)
        ]);
    };

    const handleImageFileChange = (e: React.ChangeEvent<HTMLInputElement>, indexBox: any) => {
        const files = e.target.files;
        if (files) {
            const existingImages = watchImages || [];
            const totalImages = existingImages.length + files.length;
            if (totalImages > 5) {
                showAlertModal({
                    type: 'error',
                    content: t('sentences.maximum_review_image')
                });
                return;
            }

            if (checkImageSupportFormat(files)) {
                form.setValue(`seedingComments.${index}.images`, [...(files instanceof FileList ? Array.from(files) : []), ...existingImages], { shouldValidate: true });
            } else {
                showAlertModal({
                    type: 'error',
                    content: t('form_error_validate.shop_image_upload_format')
                });
            }
            e.target.value = '';
        }
    };

    const handleRomoveImg = (indexImg: number) => {
        if (Array.isArray(watchImages) && watchImages.length > 0) {
            form.setValue(`seedingComments.${index}.images`, [
                ...watchImages.slice(0, indexImg),
                ...watchImages.slice(indexImg + 1)
            ], { shouldValidate: true });
        }
    };

    const handleDisableTime = (date: Date, limitDate: string | Date | undefined | null, isToDate: boolean) => {
        const today = new Date();
        // if (limitDate) {
        //     return isToDate ? date < limitDate : date > limitDate;
        // }
        return date > today;
    };

    return (
        <div className='mx-2 my-1 p-2 rounded-sm border border-[#e9e9e9]'>
            <div className='flex mt-2 justify-between relative'>
                <div className='w-[270px]'>
                    <FormField
                        control={form.control}
                        name={`seedingComments.${index}.nameRewiewerSeedingComment`}
                        render={({ field, fieldState }) => (
                            <FormItem>
                                <Label required>
                                    {t('words_title.seeding_cmt_reviewer', { index: index + 1 })}
                                </Label>
                                <Input
                                    {...field}
                                    className={fieldState.error ? 'border-red-500' : ''}
                                    value={field.value ?? ''}
                                    maxLength={255}
                                    onChange={e => field.onChange(e.target.value)}
                                />
                                <FormMessage className='w-5/6' />
                            </FormItem>
                        )}
                    />
                </div>
                <div className='flex flex-wrap items-center absolute right-0 top-[28px]'>
                    <Label className='label-1 leading-[18px] mr-2'>{t('words_title.seeding_cmt_hide_fullname')}</Label>
                    <FormField
                        control={form.control}
                        name={`seedingComments.${index}.incognito`}
                        render={({ field }) => (
                            <FormItem>
                                <FormControl>
                                    <Switch
                                        checked={field.value}
                                        onCheckedChange={(checked) => {
                                            field.onChange(checked);
                                        }}
                                        className='mt-1'
                                    />
                                </FormControl>
                            </FormItem>
                        )}
                    />
                </div>
            </div>

            <div className='mt-3 flex w-full'>
                <div className='w-1/2'>
                    <FormLabel className='inline-block mb-1'>{t('words_title.seeding_cmt_review_date')}</FormLabel>
                    <FormField
                        control={form.control}
                        name={`seedingComments.${index}.dateReview`}
                        render={({ field }) => (
                            <FormItem className='w-full'>
                                <FormControl>
                                    <Calendar
                                        mode='single'
                                        selected={field.value}
                                        onSelect={field.onChange}
                                        disabled={date => handleDisableTime(date, watchToDateReview, false)}
                                        useIconRemove={false}
                                        className='w-full'
                                        required
                                    />
                                </FormControl>
                            </FormItem>
                        )}
                    />
                </div>
                <div className='grow ml-2'>
                    <StepTimeSelect className='mt-[18px]' title='' name={`seedingComments.${index}.timeReview`} control={form.control} />
                </div>
            </div>

            <div className='my-2 flex flex-row h-[40px] items-center'>
                <Label className='label-1 leading-[40px] mr-3' required>{t('words_title.seeding_cmt_rate')}</Label>
                <FormField
                    control={form.control}
                    name={`seedingComments.${index}.serviceQuality`}
                    render={({ field }) => (
                        <FormItem>
                            <FormControl>
                                <StarRating
                                    value={field.value}
                                    onChange={newValue => field.onChange(newValue)}
                                />
                            </FormControl>
                            <FormMessage />
                        </FormItem>
                    )}
                />
            </div>

            <FormField
                control={form.control}
                name={`seedingComments.${index}.comment`}
                render={({ field }) => (
                    <FormItem>
                        <Label>{t('words_title.seeding_cmt_comment')}</Label>
                        <Textarea
                            className='mt-[0.375rem] resize-none h-[133px]'
                            value={field.value ?? ''}
                            maxLength={2000}
                            onChange={e => field.onChange(e.target.value)}
                        />
                    </FormItem>
                )}
            />

            <div className='mt-[1rem]'>
                <FormField
                    control={form.control}
                    name={`seedingComments.${index}.images`}
                    render={({ field: { onChange, value, ...rest } }) => (
                        <>
                            <FormItem>
                                <FormControl>
                                    <Input
                                        id={`seedingComments.${index}.images`}
                                        type='file'
                                        onChange={event => handleImageFileChange(event, index)}
                                        className='hidden'
                                        multiple={true}
                                        {...rest}
                                    />
                                </FormControl>
                                {
                                    Array.isArray(watchImages) && watchImages.length < 5 && (
                                        <Label htmlFor={`seedingComments.${index}.images`} className='flex items-center justify-center cursor-pointer border border-input rounded-lg py-3'>
                                            <ImagePlusIcon className='text-active h-6 w-6 mr-2' />
                                            <span className='text-placeholder-1 font-medium text-[0.9375rem] leading-4'>
                                                {t('button.add_image')}
                                            </span>
                                        </Label>
                                    )
                                }

                                <FormMessage />
                            </FormItem>
                            <Carousel
                                opts={{
                                    align: 'start'
                                }}
                                className='w-full my-4'
                            >
                                <CarouselContent>
                                    {
                                        Array.isArray(watchImages) && watchImages.length > 0 &&
                                        Array.from(watchImages).map((_, indexImg) => (
                                            <CarouselItem className='relative w-[20%] basis-1/5' key={`${index}-${indexImg}`}>
                                                <img className='w-full h-full object-cover aspect-square rounded' id={`preview-image-${index}-${indexImg}`} alt='' />
                                                <button
                                                    type='button'
                                                    onClick={() => handleRomoveImg(indexImg)}
                                                    className='absolute top-1 left-5 bg-black bg-opacity-80 rounded-full p-1 shadow-lg text-white'
                                                >
                                                    <XIcon className='size-3  text-white' />
                                                </button>
                                            </CarouselItem>
                                        ))
                                    }
                                </CarouselContent>
                            </Carousel>
                        </>
                    )}
                />

            </div>
            {totalBox > 1 && (
                <div className='flex flex-row-reverse'>
                    <button onClick={handleRemoveOperationItem} type='button' className='flex text-[#D9001B] cursor-pointer'>
                        <Trash2Icon className='text-[#D9001B]' />
                        {t('words_title.seeding_cmt_delete_box')}
                    </button>
                </div>
            )}

        </div>
    );
};
